export enum LIABILITY_TYPE {
  CREDIT_CARD = 'creditCard',
  PERSONAL_LOAN = 'personalLoan',
  MORTGAGE = 'mortgage',
  VEHICLE_LOAN = 'vehicleLoan',
  BUSINESS_LOAN = 'businessLoan',
  PAYDAY = 'payday',
  BNPL = 'bnpl',
  EARLY_WAGE = 'earlyWage',
  DEBT_COLLECTION = 'debtCollection',
  DEBT_CONSOLIDATION = 'debtConsolidation',
  DEBT_MANAGEMENT = 'debtManagement',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}

export const liabilityTypes = Object.values(LIABILITY_TYPE)

export type LiabilityType = (typeof LIABILITY_TYPE)[keyof typeof LIABILITY_TYPE]

export enum LIABILITY_SOURCE {
  APPLICATION = 'application',
  BANK_STATEMENT = 'bankStatement',
  CREDIT_FILE = 'creditFile',
}

export type LiabilitySource = (typeof LIABILITY_SOURCE)[keyof typeof LIABILITY_SOURCE]

export interface Liability {
  lenderName: string
  originalLenderName?: string
  displayName?: string
  originalDisplayName?: string
  reportName?: string
  type: LIABILITY_TYPE
  source: LIABILITY_SOURCE
  balance?: number
  limit?: number
  repayment?: number
  latestLimit?: number
  totalAccountHolders?: number
  isJoint?: boolean
  toConsolidateLiability?: boolean
  potentialTypes?: LIABILITY_TYPE[]
  asOfDate?: string
  loanOngoing?: string
  liabilityUuid?: string
  termRemaining?: number
  accountNumber?: string
  openDate?: string
  hasBalanceVarianceReason?: boolean
  hasLimitVarianceReason?: boolean
  hasRepaymentVarianceReason?: boolean
}

export interface BankLiability extends Liability {
  averageAmount?: number
  hasRepaymentInLast30Days?: boolean
  isBankAccountJoint?: boolean
  isOngoing: boolean
  isRepaymentVerified?: boolean
}

export interface MortgageLiability extends Liability {
  propertyValue: number
}

export interface PersonalLoanLiability extends Liability {
  originalAmount: number
}

export interface VehicleLoanLiability extends Liability {
  originalAmount: number
  lookingToTradeIn: string | boolean
}

export interface CreditCardLiability extends Liability {
  interestRate?: number
}

export type AppLiability =
  | Liability
  | MortgageLiability
  | PersonalLoanLiability
  | VehicleLoanLiability
  | CreditCardLiability

export type LiabilityMap = Map<
  string,
  Liability | MortgageLiability | PersonalLoanLiability | VehicleLoanLiability | CreditCardLiability
>

export type LiabilityAlias = {
  alias: string
  displayName: string
  reportName?: string
  types?: LIABILITY_TYPE[]
}

export type LiabilityAliasMap = Map<string, LiabilityAlias>

export type LiabilityAliasList = LiabilityAlias[]

export type MismatchField = {
  source: LIABILITY_SOURCE[]
  mismatchPercentage: number
}

export type LiabilityMismatchField = {
  balance?: MismatchField
  limit?: MismatchField
  repayment?: MismatchField
}

export type LiabilityMismatch = {
  worstMismatchPercentage: number
} & LiabilityMismatchField

export interface LIABILITY_UUID_INFO {
  source: LIABILITY_SOURCE
  liabilityUuid: string
}

export type MatchedLiability = {
  displayName: string
  verified: boolean
  appLiability?: AppLiability
  bankLiability?: BankLiability
  creditLiability?: Liability
  mismatches?: LiabilityMismatch
  liabilityUuids?: LIABILITY_UUID_INFO[]
}

export type LiabilityVerification = {
  declared: MatchedLiability[]
  underDeclared: MatchedLiability[]
  overDeclared: MatchedLiability[]
}

export enum MISSING_LIABILITY_DYNAMIC_REQUEST_RECOGNISE_RESPONSE {
  FORGOT_LIABILITY = 'Yes - I forgot to enter this liability.',
  CONSOLIDATE_LIABILITY = 'Yes - I want to trade in or consolidate this liability.',
  CLOSED_LIABILITY = 'Yes - This liability has been repaid and closed.',
  UNRECOGNISED_LIABILITY = 'No - This is not my liability.',
}

export enum MISSING_LIABILITY_DYNAMIC_REQUEST_FIELD {
  RECOGNISE_LIABILITY = 'Do you recognise this liability?',
  LENDER_NAME = 'Lender Name',
  LIABILITY_TYPE = 'Liability Type',
  LIMIT = 'Limit',
  AMOUNT_OWING = 'Amount Owing',
  ORIGINAL_AMOUNT = 'Original Amount',
  MONTHLY_REPAYMENT = 'Monthly Repayment',
  PROPERTY_VALUE = 'Property Value',
  IS_JOINT = 'Is this a joint liability?',
  TO_CONSOLIDATE = 'Do you want to consolidate this liability?',
  DESCRIPTION = 'Description',
  LATEST_BALANCE = 'Latest Balance',
}
